import { AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CONFIG } from 'app/config/config';
import {
  EAuthState,
  IRegisterUserPayload,
} from 'app/core/services/auth/auth.model';
import { AuthService } from 'app/core/services/auth/auth.service';
import { LoaderComponent } from 'app/shared/components/loader/loader.component';
import { filter, switchMap } from 'rxjs';
import { AlertComponent } from '../../../shared/components/alert/alert.component';

@Component({
  selector: 'casa-doc-sign-in',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    AlertComponent,
    AsyncPipe,
    LoaderComponent,
  ],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
})
export class SignInComponent {
  authService = inject(AuthService);
  eAuthState = EAuthState;
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  isSignUpMode = signal(this.route.snapshot.data['signUp'] === true);
  successMessage = signal<string | null>(null);
  private successMessageTimeoutId?: number;

  form = new FormGroup({
    identifier: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.successMessageTimeoutId) {
        window.clearTimeout(this.successMessageTimeoutId);
      }
    });

    afterNextRender(() => {
      const queryParams = this.route.snapshot.queryParamMap;
      if (queryParams.get('registration-success') === 'true') {
        this.showSuccessMessage(
          'Registrazione avvenuta con successo, conferma registrazione cliccando sulla mail.'
        );
      } else if (queryParams.get('signin-confirmation') === 'true') {
        this.showSuccessMessage(
          'Account confermato con successo, ora puoi accedere.'
        );
      }

      if (this.authService.isTokenValid()) {
        this.router.navigate([CONFIG.urls.HOME]);
      } else {
        this.authService.authState$
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(
              (authState: EAuthState) =>
                authState === EAuthState.TOKEN_RETRIEVED
            ),
            switchMap(() => this.authService.loginUserWithToken())
          )
          .subscribe(() => {
            this.authService.handleRedirect();
          });
      }
    });
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    if (this.isSignUpMode()) {
      const { identifier, password } = this.form.getRawValue();
      this.successMessage.set(null);
      this.authService
        .registerUser({
          email: identifier,
          password,
        } as IRegisterUserPayload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.form.reset({
              identifier: '',
              password: '',
            });
            this.authService.clearMessages();
            this.router.navigate(['/sign-in'], {
              queryParams: { 'registration-success': 'true' },
            });
          },
        });
      return;
    }

    this.authService.handleAuthentication(this.form.getRawValue());
  }

  toggleAuthMode() {
    const next = !this.isSignUpMode();
    this.successMessage.set(null);
    this.authService.clearMessages();
    this.router.navigate([next ? '/sign-up' : '/sign-in']);
  }

  private showSuccessMessage(message: string) {
    this.successMessage.set(message);

    if (this.successMessageTimeoutId) {
      window.clearTimeout(this.successMessageTimeoutId);
    }

    this.successMessageTimeoutId = window.setTimeout(() => {
      this.successMessage.set(null);
    }, 6000);
  }
}
